import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import { Box, Typography, styled } from '@mui/material';
import React, { ChangeEvent, useRef, useState } from 'react';
import DeleteIcon from './icons/DeleteIcon';

interface BaseFileUploadProps {
  label?: string;
  accept?: string;
  required?: boolean;
  width?: string;
  error?: string;
  disabled?: boolean;
}

interface SingleFileUploadProps extends BaseFileUploadProps {
  multiple?: false;
  onChange: (file: File | null) => void;
  value?: File | null | string;
}

interface MultipleFileUploadProps extends BaseFileUploadProps {
  multiple: true;
  onChange: (files: File[] | null) => void;
  value?: File[] | null;
}

type CustomFileUploadProps = SingleFileUploadProps | MultipleFileUploadProps;

const UploadBox = styled(Box)({
  border: '1px solid #D0D5DD',
  borderRadius: '8px',
  padding: '8px 14px',
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  cursor: 'pointer',
  height: '31px',
  backgroundColor: '#F1F3FF',
  '&:hover': {
    borderColor: '#6E3FF3',
  },
  '&.error': {
    borderColor: '#F04438',
  },
  '&.disabled': {
    backgroundColor: '#00000014',
    cursor: 'not-allowed',
    borderColor: '#00000014',
  },
});

const HiddenInput = styled('input')({
  display: 'none',
});

const Label = styled(Typography)({
  color: '#666666',
  fontSize: '14px',
  marginBottom: '8px',
  display: 'flex',
  alignItems: 'center',
  '& .required': {
    color: '#F04438',
    marginLeft: '4px',
  },
});

const UploadText = styled(Typography)({
  color: '#6E3FF3',
  fontSize: '14px',
  flex: 1,
});

const ErrorText = styled(Typography)({
  color: '#F04438',
  fontSize: '14px',
  marginTop: '6px',
});

const PreviewBox = styled(Box)<{ width?: string }>(({ width }) => ({
  width: width || '174px',
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  marginBottom: '8px',
  padding: '8px 14px',
  backgroundColor: '#F1F3FF',
  borderRadius: '8px',
  height: '31px',
}));

const FileName = styled(Typography)({
  color: '#101828',
  fontSize: '12px',
  flex: 1,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
});

const Delete = styled('div')({
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  '&:hover': {
    opacity: 0.8,
  },
  '&.disabled': {
    opacity: 0.5,
    cursor: 'not-allowed',
  },
});

const IMAGE_EXTENSIONS = new Set([
  'png',
  'jpg',
  'jpeg',
  'gif',
  'webp',
  'svg',
  'bmp',
  'ico',
  'heic',
  'avif',
]);

function acceptRequiresImageValidation(accept: string): boolean {
  return accept.split(',').some((part) => {
    const p = part.trim().toLowerCase();
    return p === 'image/*' || p.startsWith('image/');
  });
}

function isLikelyImageFile(file: File): boolean {
  if (file.type.startsWith('image/')) return true;
  const ext = file.name.split('.').pop()?.toLowerCase();
  return ext ? IMAGE_EXTENSIONS.has(ext) : false;
}

const FileUploadField: React.FC<CustomFileUploadProps> = ({
  label,
  accept = '.pdf,.docx,.doc',
  required = false,
  onChange,
  width,
  error,
  value,
  disabled = false,
  multiple = false,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [internalError, setInternalError] = useState('');
  const displayError = error || internalError;

  const handleClick = () => {
    if (disabled) return;
    inputRef.current?.click();
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (disabled) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleClick();
    }
  };

  const resetFileInput = () => {
    if (inputRef.current) {
      inputRef.current.value = '';
    }
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (disabled) return;
    const files = event.target.files;
    const imageAccept = acceptRequiresImageValidation(accept);

    if (multiple) {
      if (files && files.length > 0) {
        const fileArray = Array.from(files);
        if (imageAccept && fileArray.some((f) => !isLikelyImageFile(f))) {
          resetFileInput();
          setInternalError('Invalid file type. Please upload an image.');
          return;
        }
        setInternalError('');
        (onChange as (files: File[] | null) => void)(fileArray);
      } else {
        setInternalError('');
        (onChange as (files: File[] | null) => void)(null);
      }
    } else {
      const file = files?.[0] || null;
      if (file && imageAccept && !isLikelyImageFile(file)) {
        resetFileInput();
        setInternalError('Invalid file type. Please upload an image.');
        return;
      }
      setInternalError('');
      (onChange as (file: File | null) => void)(file);
    }
  };

  const handleDelete = (index?: number) => {
    if (disabled) return;
    setInternalError('');
    
    if (multiple) {
      // Multiple file mode
      if (Array.isArray(value) && index !== undefined) {
        const newFiles = value.filter((_, i) => i !== index);
        (onChange as (files: File[] | null) => void)(newFiles.length > 0 ? newFiles : null);
      } else {
        (onChange as (files: File[] | null) => void)(null);
      }
    } else {
      // Single file mode (backward compatibility)
      (onChange as (file: File | null) => void)(null);
    }
    
    resetFileInput();
  };

  return (
    <Box>
      {label && (
        <Label className="input-field-label">
          {label}
          {required && <span>*</span>}
        </Label>
      )}
      <UploadBox
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        tabIndex={disabled ? -1 : 0}
        role="button"
        aria-label={label ? `Upload ${label}` : 'Upload file'}
        className={`${displayError ? 'error' : ''} ${disabled ? 'disabled' : ''}`}
        width={width}
        sx={{ marginBottom: '8px', outline: 'none', '&:focus-visible': { outline: '2px solid #6941C6', outlineOffset: '2px' } }}
      >
        {multiple ? (
          // Multiple file mode
          (!value || (Array.isArray(value) && value.length === 0)) && <UploadText>Upload Files</UploadText>
        ) : (
          // Single file mode (backward compatibility)
          !value && <UploadText>Upload</UploadText>
        )}
        
        {multiple && Array.isArray(value) && value.length > 0 && (
          <>
            <FileName>{value.length} file{value.length > 1 ? 's' : ''} selected</FileName>
            {!disabled && <Delete onClick={() => handleDelete()}>
              <DeleteIcon />
            </Delete>}
          </>
        )}
        
        {!multiple && value && !Array.isArray(value) && (
          <>
            <FileName>{value instanceof File ? value.name : value}</FileName>
            {!disabled && <Delete onClick={() => handleDelete()}>
              <DeleteIcon />
            </Delete>}
          </>
        )}
        <FileUploadOutlinedIcon sx={{ color: '#6E3FF3', fontSize: 20, cursor: 'pointer' }} aria-hidden />
      </UploadBox>
      {multiple && Array.isArray(value) && value.length > 0 && (
        <Box sx={{ marginBottom: '8px' }}>
          {value.map((file, index) => (
            <PreviewBox key={index} width={width}>
              <FileName>{file.name}</FileName>
              {!disabled && <Delete onClick={() => handleDelete(index)}>
                <DeleteIcon />
              </Delete>}
            </PreviewBox>
          ))}
        </Box>
      )}
      <HiddenInput
        type="file"
        ref={inputRef}
        accept={accept}
        onChange={handleChange}
        multiple={multiple ? true : undefined}
      />
      {displayError && <ErrorText>{displayError}</ErrorText>}
    </Box>
  );
};

export default FileUploadField; 